<template>
  <div
    class="ai-bg translation-container"
    v-if="open"
    aria-live="polite"
    aria-atomic="true"
    aria-relevant="additions text"
    ref="translationContents">
    <div class="translation-contents">
      <span
        @click="closeTranslation"
        @keypress.enter="closeTranslation"
        tabindex="0"
        class="close-button"
        role="button"
        aria-label="close translation pop-up"
      >x</span>
      <div v-if="loading" class="loading">
        <i class="fa fa-circle-o-notch fa-spin fa-2x" />
        <p role="status" aria-busy="true">Translating...</p>
      </div>
      <div v-else-if="loadError" class="error">
        <p class="alert alert-info" role="alert">
          <i class="fa fa-info-circle" />We're not able to translate goals right now. Try again
          later.
        </p>
      </div>
      <div v-else>
        <CopyTrackWrapper
          type="goal_translation"
          :title="translatedGoalTitle"
          :text="translatedGoalText">
          <p role="status" class="sr-only">Goal translation completed.</p>
          <h4 class="title">{{ translatedGoalTitle }}
            <tooltip :text="'Copy'" placement="bottom">
              <CopyButton
                btn-class="btn btn-default btn-xs borderless-button"
                :copy-text="wikiToPlainText(translatedGoalText)"
                success-text="Copied Translated Goal"
                error-text="Error copying text. Use Ctrl+C instead."
                :aria-label="`Copy translated goal`"
                :tooltip-placement="'bottom'">
                <span
                  class="material-icons-outlined borderless-button-content copy"
                  :aria-label="`Copy translated goal`">
                  content_copy
                </span>
              </CopyButton>
            </tooltip>
          </h4>
          <WikiText
            :text="translatedGoalText"
            :dir="isRTL ? 'rtl' : 'ltr'"
            :class="{'text-right': isRTL}"/>
        </CopyTrackWrapper>
        <div class="inline-buttons">
          <AIBetaBadge />
          <FeedbackButtons :event-data="feedbackEventData" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { translateGoalText } from '@/api/goaldetail'
import { mapGetters } from 'vuex'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import CopyButton from '@/components/CopyButton.vue'
import FeedbackButtons from '@/components/FeedbackButtons.vue'
import AIBetaBadge from '@/components/AIBetaBadge.vue'
import WikiText from '@/components/WikiText.vue'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'

export default {
  name: 'TranslationContainer',
  components: { FeedbackButtons, CopyButton, CopyTrackWrapper, AIBetaBadge, WikiText },
  props: {
    goal_title: {
      type: String,
      default: '',
    },
    goal_text: {
      type: String,
      default: '',
    },
    goal_uuid: {
      type: String,
      default: '',
    },
    edited: {
      type: Boolean,
      default: false,
    },
    externalOpen: {
      type: Boolean,
      default: false,
    },
    requestedLanguage: {
      type: String,
      default: '',
    },
  },
  async mounted() {
    if (this.open && this.translationLanguage) {
      await this.generateTranslation()
    }
  },
  computed: {
    ...mapGetters({
      isTrial: 'user/isTrial',
      isLicenseExpired: 'user/isLicenseExpired',
      goalTranslationLanguagesRtoL: 'customanchor/goalTranslationLanguagesRtoL',
    }),
    feedbackEventData() {
      return {
        goal_uuid: this.goal_uuid,
        goal_title: this.goal_title,
        translation_language: this.translationLanguage,
        translated_text: this.translatedGoalTitle + ' ' + this.translatedGoalText,
      }
    },
    isRTL() {
      return this.goalTranslationLanguagesRtoL.includes(this.translationLanguage);
    },
  },
  data() {
    return {
      translatedGoalText: '',
      translatedGoalTitle: '',
      loading: false,
      open: this.externalOpen,
      loadError: false,
      translationLanguage: this.requestedLanguage,
    }
  },
  watch: {
    async goal_text(val) {
      if (this.open) {
        if (val) {
          await this.generateTranslation()
        }
        if (val === '') {
          this.closeTranslation()
        }
      }
    },
    async externalOpen(newVal) {
      this.open = newVal
      if (newVal && this.translationLanguage) {
        await this.generateTranslation()
      }
    },
    async requestedLanguage(newVal) {
      this.translationLanguage = newVal
      if (this.open && newVal) {
        await this.generateTranslation()
      }
    },
  },
  eventContext() {
    return {
      content_type: 'goal text',
      translation_language: this.translationLanguage,
      untranslated_text: this.goal_title + ' ' + this.goal_text,
    }
  },
  methods: {
    wikiToPlainText,
    async generateTranslation() {
      if (!this.open) {
        return
      }

      if (this.goal_text === '' || this.goal_uuid === '' || this.goal_title === '' || !this.translationLanguage) {
        console.warn('Cannot generate translation - missing required data:', {
          goal_text: !!this.goal_text,
          goal_uuid: !!this.goal_uuid,
          goal_title: !!this.goal_title,
          translationLanguage: this.translationLanguage
        })
        this.loadError = true
        return
      }
      this.loading = true
      this.loadError = false
      this.translatedGoalText = ''
      this.translatedGoalTitle = ''
      this.$sendEvent('Initiated Translation')
      let translationResponse
      try {
        translationResponse = await translateGoalText(this.goal_uuid, this.goal_text, this.edited, this.translationLanguage)
        this.translatedGoalText = translationResponse.goal_text
        this.translatedGoalTitle = translationResponse.goal_title
      } catch (e) {
        console.error('error generating translation: ', e)
        this.loading = false
        this.loadError = true
        this.$nextTick(() => {
          this.$refs.translationContents?.focus()
        })
        return
      }

      this.$sendEvent('Translated Text', {
        translated_text: this.translatedGoalTitle + ' ' + this.translatedGoalText,
      })
      this.loading = false
      this.$nextTick(() => {
        this.$refs.translationContents?.focus()
      })
    },
    closeTranslation() {
      this.$sendEvent('Dismissed Translation')
      this.open = false
      this.$emit('update:externalOpen', false)
      this.translationLanguage = ''
    },
  },
}
</script>
<style lang="less" scoped>
@import '~@/variables.less';

.translation-container {
  border-radius: 4px;
  padding: 2px;
  margin-top: 20px;
}
.translation-contents {
  background: white;
  position: relative;
  padding: 16px 24px 16px 24px;
  border-radius: 2px;
}
.close-button {
  position: absolute;
  top: 14px;
  right: 16px;
  padding: 2px 8px 4px 8px;
  font-size: 18px;
  font-weight: 300;
  line-height: 20px;
  &:hover {
    background: #e6e6e6;
    border-radius: 9999px;
  }
}
.alert {
  font-size: var(--text-md);
  display: inline-block;
  border-radius: 4px;
  color: @gray-dark;
  margin-bottom: 0px;
  padding: 8px;
  .fa-info-circle {
    padding-right: 8px;
  }
}
.title {
  font-weight: 500;
  font-size: 20px;
  display: inline-block;
}
.loading {
  text-align: center;
  .fa-spin {
    color: @gb_blue_medium;
  }
  p {
    font-size: var(--text-md);
  }
}
.inline-buttons {
  display: flex;
  line-height: 20px;
  padding-top: 8px;
  padding-left: 0;
  column-gap: 12px;
}
/deep/ .ai-beta-badge-wrapper {
  margin-left: 0 !important;
}
</style>
